import client from '@graphql/client'
import tableauNavigationsDefaultDashboardGql from '@graphql/queries/tableau-navigations-default-dashboard'
import tableauNavigationsGql from '@graphql/queries/tableau-navigations'
import { getBestDashboardPath, generateDashboardPath, findBestDashboard } from './dashboard-selection.js'

const getDefaultDashboardPath = async (user) => {
  // Use the centralized dashboard selection logic
  const bestPath = await getBestDashboardPath(user, user.client?.clientModules)
  // If we got a path from cached navigation, return it
  if (bestPath) {
    return bestPath
  }

  // If no cached navigation or no dashboard found, we need to query Tableau for full navigation
  try {
    const { data } = await client.query({
      query: tableauNavigationsGql,
      variables: {
        navigationType: 'appExpandNav',
        client: user?.client?.name
      }
    })

    if (data.tableauNavigations) {
      const navigationData = JSON.parse(data.tableauNavigations)
      const bestDashboard = findBestDashboard(navigationData, user.homepage)
      if (bestDashboard) {
        const path = generateDashboardPath(bestDashboard)
        if (path) return path
      }
    }
  } catch (error) {
    console.warn('Failed to fetch navigation from Tableau:', error)
  }

  // Fallback: try the old single dashboard approach if full navigation fails
  let defaultProject = determineDefaultProject(user.client?.clientModules)
  if (!defaultProject) {
    // Fallback to first available module
    defaultProject = user.client?.clientModules?.[0]?.module?.name
  }

  if (defaultProject) {
    try {
      const { data } = await client.query({
        query: tableauNavigationsDefaultDashboardGql,
        variables: {
          defaultProject
        }
      })

      if (data.tableauNavigationsDefaultDashboard) {
        const defaultDashboard = JSON.parse(data.tableauNavigationsDefaultDashboard)
        return generateDashboardPath({
          contentUrl: defaultDashboard.contentUrl,
          defaultView: defaultDashboard.defaultView,
          name: defaultDashboard.name || defaultProject
        })
      }
    } catch (error) {
      console.warn('Failed to fetch default dashboard from Tableau:', error)
    }
  }

  // No valid dashboard found - provide a generic fallback
  console.warn('No valid dashboard found for user, using generic fallback')
  return '/dashboard' // Generic dashboard path that should redirect appropriately
}

/**
 * Determine the best default project based on client modules and priority rules
 * @param {Array} clientModules - Array of client modules
 * @returns {string|null} - The best default project name
 */
const determineDefaultProject = (clientModules) => {
  if (!clientModules || !Array.isArray(clientModules)) {
    return null
  }

  // Priority order based on the new rules
  const priorityModules = [
    'Executive Dashboard',
    'ScripLogic', // For Scorecard in ScripLogic
    'FinanceLogic', // For Scorecard in FinanceLogic
    'RebateLogic', // For RebateLogic Submission
    'Home Scorecard' // For any Scorecard
  ]

  // Find the first available module in priority order
  for (const moduleName of priorityModules) {
    const module = clientModules.find(cm => cm.module.name === moduleName)
    if (module) {
      return moduleName
    }
  }

  return null
}

export default getDefaultDashboardPath
